import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import "@nxu/ninja-keys";
import API from "@/services/api";
import { FaSearch } from "react-icons/fa";

export default function CommandPalette() {
  const { user } = useSelector((state) => state.Auth);
  const navigate = useNavigate();
  const ninjaKeys = useRef(null);
  let debounceTimeout = null;

  const menu = [
    { id: "home", title: "Home", handler: () => navigate("/"), roles: ["admin", "user"] },
    { id: "articles", title: "Articles", handler: () => navigate("/articles"), roles: ["admin", "user"] },
    { id: "account", title: "Account", handler: () => navigate("/account"), roles: ["admin", "user", "client", "applicant"] },
    { id: "perks", title: "Perks", handler: () => navigate("/perks"), roles: ["admin", "user", "client"] },
    { id: "project", title: "Project", handler: () => navigate("/project"), roles: ["admin", "user", "client"] },
    { id: "training_product", title: "Training Product", handler: () => navigate("/training_product"), roles: ["admin", "user"] },
    { id: "check", title: "Check", handler: () => navigate("/check"), roles: ["admin", "user"] },
    { id: "performance", title: "Performance", handler: () => navigate("/performance"), roles: ["admin"] },

    { id: "brain", title: "Brain", handler: () => navigate("/brain"), roles: ["admin"] },
    { id: "alert", title: "Alert", handler: () => navigate("/alert"), roles: ["admin", "user"] },
    { id: "activity", title: "Activity", handler: () => navigate("/activity"), roles: ["admin", "user"] },
    { id: "meeting_ai", title: "Meeting AI", handler: () => navigate("/meeting_ai"), roles: ["admin", "user"] },
    { id: "sales", title: "Sales", handler: () => navigate("/sales"), roles: ["admin", "user"] },
    { id: "payment", title: "Payment", handler: () => navigate("/payment"), roles: ["admin", "user"] },
    { id: "birthdays", title: "Birthdays", handler: () => navigate("/birthdays"), roles: ["admin", "user"] },
    { id: "days_off", title: "Days Off", handler: () => navigate("/days-off"), roles: ["admin", "user"] },
    { id: "debate", title: "Debate", handler: () => navigate("/debate"), roles: ["admin", "user"] },
    { id: "assets", title: "Assets", handler: () => navigate("/assets"), roles: ["admin", "user"] },
    { id: "agents", title: "Agents", handler: () => navigate("/agents"), roles: ["admin", "user"] },
    { id: "report", title: "Report", handler: () => navigate("/report"), roles: ["admin", "user"] },
    { id: "cashout", title: "Cashout", handler: () => navigate("/cashout"), roles: ["admin", "user"] },
    { id: "people", title: "People", handler: () => navigate("/people"), roles: ["admin", "user"] },

    { id: "claude", title: "Claude", handler: () => navigate("/claude"), roles: ["admin", "user"] },
    { id: "helps", title: "Helps", handler: () => navigate("/helps"), roles: ["admin", "user"] },
    { id: "invoice", title: "Invoice", handler: () => navigate("/invoice"), roles: ["admin", "user"] },
    { id: "bank", title: "Bank", handler: () => navigate("/bank"), roles: ["admin", "user"] },
    { id: "organisation", title: "Organisation", handler: () => navigate("/organisation"), roles: ["admin", "user"] },
    { id: "organigramme", title: "Organigramme", handler: () => navigate("/organigramme"), roles: ["admin", "user"] },
    { id: "device", title: "Device", handler: () => navigate("/device"), roles: ["admin", "user"] },
    { id: "channel", title: "Channel", handler: () => navigate("/channel"), roles: ["admin", "user"] },
    { id: "crm", title: "CRM", handler: () => navigate("/crm"), roles: ["admin"] },
  ]
    .filter((item) => {
      if (item.roles) return item.roles.includes(user.role);
      return true;
    })
    .map((item) => ({ ...item, section: "Menu" }));

  const searchFn = async (event) => {
    const { search } = event.detail;

    if (!search) {
      ninjaKeys.current.data = menu;
      return;
    }

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    debounceTimeout = setTimeout(async () => {
      try {
        const projectPromise = API.post(`/project/search`, { search }).catch((e) => {
          console.error("Project search failed", e);
          return { data: { projects: [] } };
        });
        const userPromise = API.post(`/user/search`, { search }).catch((e) => {
          console.error("User search failed", e);
          return { data: { users: [] } };
        });
        const workshopPromise = API.post(`/workshop/search`, { search }).catch((e) => {
          console.error("Workshop search failed", e);
          return [];
        });

        const [projectResponse, userResponse, workshopResponse] = await Promise.all([projectPromise, userPromise, workshopPromise]);

        const items = [
          ...(Array.isArray(projectResponse.data.projects) ? projectResponse.data.projects : []).map((p) => ({
            id: `${p.name}-project`,
            title: `[Project] ${p.name}`,
            keywords: search,
            handler: () => navigate(`/project/${p._id}`),
            section: "Projects",
          })),
          ...(Array.isArray(userResponse.data.users) ? userResponse.data.users : []).map((u) => ({
            id: `${u.name}-user`,
            title: `[User] ${u.name}`,
            keywords: search,
            handler: () => navigate(`/people?user_modal_id=${u._id}&user_modal_tab=about`),
            section: "People",
          })),
          ...(Array.isArray(workshopResponse) ? workshopResponse : []).map((w) => ({
            id: `${w.name}-workshop`,
            title: `[Workshop] ${w.name}`,
            keywords: search,
            handler: () => navigate(`/learn/${w._id}`),
            section: "Workshops",
          })),
        ];

        ninjaKeys.current.data = [...menu, ...items];
      } catch (e) {
        toast.error("Something went wrong");
        console.error(e);
      }
    }, 150);
  };

  const reset = () => {
    ninjaKeys.current.data = menu;
  };

  useEffect(() => {
    if (ninjaKeys.current) {
      ninjaKeys.current.data = menu;
      ninjaKeys.current.addEventListener("change", searchFn);
      ninjaKeys.current.addEventListener("selected", reset);
    }
    return () => {
      removeEventListener("change", searchFn);
      removeEventListener("selected", reset);
    };
  }, []);

  if (!["user", "admin"].includes(user.role)) return <div />;

  return (
    <>
      <button
        type="button"
        onClick={() => ninjaKeys.current.open()}
        className="focus:outline-none text-xs flex border border-gray-300 hover:border-sky-700 rounded px-1.5 h-[2.35rem] gap-1 items-center md:w-64">
        <FaSearch className="text-gray-500 text-sm mx-1 flex-none" />
        <p className="text-gray-600 hidden sm:block">Quick Access</p>
        <div className="ml-auto gap-0.5 hidden sm:flex">
          <p className="text-gray-600 border-[1px] border-gray-400 px-1.5 py-0.5 rounded">CMD</p>
          <p className="text-gray-600 border-[1px] border-gray-400 px-1.5 py-0.5 rounded">K</p>
        </div>
      </button>
      <ninja-keys ref={ninjaKeys} placeholder="Search for a user, project, workshop or folk item"></ninja-keys>
    </>
  );
}
